/** @jsx jsx */
import { jsx } from '@theme-ui/core';
import { CheckboxCardGroup, CheckboxCard, Loader } from '@uberall/zenit';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import styles from './modal-content.module.css';
import { SelectionListItem } from '../../../../contexts/types';
import Message from '../../../Message';
import NoItemsView from './no-items-view';
import ModalContentContainer from './modal-content-container';
import { isPersonalAccount } from '../../utils';

type SelectionModalContentProps = {
    items: SelectionListItem[];
    subtitle: string;
    containerClassName?: string;
    isFetchingNextPage: boolean;
    isLoading: boolean;
    onToggle: (id: string) => void;
    fetchNextPage: () => void;
};

const SelectionModalContent = ({
    items,
    subtitle,
    onToggle,
    containerClassName,
    fetchNextPage,
    isLoading,
    isFetchingNextPage,
}: SelectionModalContentProps) => {
    const { ref: refHiddenElementForLazyLoad, inView } = useInView();

    useEffect(() => {
        if (inView) {
            fetchNextPage();
        }
    }, [inView, fetchNextPage]);

    const getLabel = (item: SelectionListItem) => {
        if (item.alreadyGrantedAccess) {
            return (
                <Message code="app.request.access.public.step.2.google.profile.modal.label.access.granted" />
            );
        } else if (isPersonalAccount(item)) {
            return (
                <Message code="app.request.access.public.step.2.google.profile.modal.label.personal.account" />
            );
        }
        return '';
    };
    const getLabelVariant = (item: SelectionListItem) => {
        if (isPersonalAccount(item)) {
            return 'active';
        }
        // undefined returns the default variant
        return undefined;
    };

    const isNonPersonalAccountSelected = items.some((item) => {
        return !isPersonalAccount(item) && item.checked;
    });

    const isItemDisabled = (item: SelectionListItem) => {
        const isPersonalAccountSelected = items.some((item) => {
            return isPersonalAccount(item) && item.checked;
        });

        const isPersonalAccountSelectedAndCurrentItemIsNotPersonal =
            isPersonalAccountSelected && !isPersonalAccount(item);

        const isNonPersonalAccountSelectedAndCurrentItemIsPersonal =
            isNonPersonalAccountSelected && isPersonalAccount(item);

        return (
            isPersonalAccountSelectedAndCurrentItemIsNotPersonal ||
            isNonPersonalAccountSelectedAndCurrentItemIsPersonal ||
            item.alreadyGrantedAccess
        );
    };

    if (isLoading) {
        return (
            <ModalContentContainer containerClassName={containerClassName}>
                <Loader className={styles.loader} />
            </ModalContentContainer>
        );
    } else if (items.length === 0) {
        return (
            <ModalContentContainer containerClassName={containerClassName}>
                <NoItemsView subtitle={subtitle} />
            </ModalContentContainer>
        );
    }
    return (
        <ModalContentContainer containerClassName={containerClassName}>
            <Message code={subtitle} />
            <CheckboxCardGroup sx={{ padding: 0 }} className={styles.checkboxCardGroup}>
                {items.map((item) => {
                    return (
                        <div key={item.id}>
                            <CheckboxCard
                                checked={item.checked}
                                description1={item.subtitle || ''}
                                description2={item.subtitle2}
                                title={item.title}
                                value={item.id}
                                onChange={() => onToggle(item.id)}
                                label={getLabel(item)}
                                labelVariant={getLabelVariant(item)}
                                disabled={isItemDisabled(item)}
                                data-testid="selection-modal-content-checkbox-card"
                            />
                            {isPersonalAccount(item) && item.checked && (
                                <Message
                                    className={styles.itemInfoText}
                                    code="app.request.access.public.step.2.google.profile.modal.warning.personal.account.selected"
                                />
                            )}
                            {isPersonalAccount(item) && isNonPersonalAccountSelected && (
                                <Message
                                    className={styles.itemInfoText}
                                    code="app.request.access.public.step.2.google.profile.modal.warning.non.personal.account.selected"
                                />
                            )}
                        </div>
                    );
                })}
            </CheckboxCardGroup>
            {isFetchingNextPage && <Loader className={styles.loaderNextPage} />}
            <div className={styles.visibilityTrigger} ref={refHiddenElementForLazyLoad}>
                &nbsp;
            </div>
        </ModalContentContainer>
    );
};

export default SelectionModalContent;
